import { useEffect, useState } from 'react';
import { T } from '@design/index';
import { patientApi } from '../api';

/**
 * The patient's own profile photo.
 *
 * `GET /profile/photo/:file` is authenticated, so an `<img src>` pointed at it
 * gets a 401 and silently falls back to initials — which is why this fetches
 * the file as a blob and renders it from an object URL, the same way the app
 * sends its own auth header.
 *
 * No photo is not a failure: initials are the honest answer, and they are what
 * most accounts will show.
 */
export default function PatientAvatar({
  avatarUrl,
  name,
  size = 72,
}: {
  readonly avatarUrl?: string | null;
  readonly name?: string;
  readonly size?: number;
}) {
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    if (!avatarUrl) { setSrc(null); return; }
    let alive = true;
    let objectUrl: string | null = null;
    patientApi.profilePhotoBlob(avatarUrl)
      .then((blob) => {
        if (!alive) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch(() => { if (alive) setSrc(null); });
    return () => {
      alive = false;
      // Object URLs are held by the document until revoked; leaving them is a
      // leak on a page the patient may open many times.
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [avatarUrl]);

  const initials = (name ?? '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('') || '·';

  return (
    <div
      style={{
        width: size, height: size, borderRadius: '50%', overflow: 'hidden',
        background: `${T.teal}14`, border: `1px solid ${T.border}`,
        display: 'grid', placeItems: 'center', flexShrink: 0,
      }}
    >
      {src ? (
        <img src={src} alt="" width={size} height={size} style={{ width: size, height: size, objectFit: 'cover' }} />
      ) : (
        <span style={{ fontSize: size * 0.34, fontWeight: 800, color: T.teal, fontFamily: T.font }}>
          {initials}
        </span>
      )}
    </div>
  );
}
